/**
 * Writing a key into a text field, from a keyboard the app draws itself.
 *
 * Typing has to be indistinguishable, to the rest of the app, from a person
 * typing: the search box's debounce, the setup form's validation, the `✕`
 * clear all listen for `input`. React additionally keeps its own copy of the
 * last value it rendered and ignores an `input` event whose value it believes it
 * already has — so a plain `field.value = next` would be swallowed and the app's
 * handlers would never run. Going through the prototype's own setter is what
 * keeps React's copy in step, which is why this lives in one place rather than
 * being spelled out at each key.
 *
 * `maxLength` is enforced here: assigning a value skips the limit the browser
 * applies to real typing.
 */

type Field = HTMLInputElement | HTMLTextAreaElement;

/** Put `text` at the end of the field's value. False when nothing changed. */
export function insertIntoField(field: Field, text: string): boolean {
  const limit = field.maxLength;
  let next = field.value + text;
  if (limit >= 0 && next.length > limit) next = next.slice(0, limit);
  if (next === field.value) return false;
  setFieldValue(field, next);
  return true;
}

/** Remove the last character. False when the field was already empty. */
export function backspaceInField(field: Field): boolean {
  if (!field.value) return false;
  setFieldValue(field, field.value.slice(0, -1));
  return true;
}

function setFieldValue(field: Field, value: string): void {
  // The element's OWN prototype, not `HTMLInputElement.prototype`: that global
  // is absent in some environments (the test suite's jsdom, notably — see
  // keyboardOnDemand.ts, which duck-types for the same reason), while an
  // instance always carries the right prototype with the native setter on it.
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field) as object, "value")?.set;
  if (setter) setter.call(field, value);
  else field.value = value;
  field.dispatchEvent(new Event("input", { bubbles: true }));
}
